import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Gauge, Zap, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact usage meter for the Generator page.
 *
 * Pulls /api/auth/daily-usage (now monthly-bucketed) and renders a coloured
 * progress bar with "X / Y generations used · Resets in Z days". Switches to
 * a soft "upgrade" CTA when the user is approaching their cap.
 *
 * Hides itself for unauthenticated VIP visitors and for admins (unlimited).
 */
const UsageMeter = ({ authHeader, isAuthenticated, refreshKey }) => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/api/auth/daily-usage`, { headers: authHeader() })
      .then((res) => {
        if (!cancelled) setUsage(res.data);
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authHeader, refreshKey]);

  if (loading || !usage || usage.plan === 'admin' || !usage.limit) return null;

  const {
    count = 0,
    limit = 0,
    plan = 'trial',
    remaining = 0,
    period_type = 'monthly',
    resets_at = null,
  } = usage;
  const percent = limit > 0 ? Math.min(100, Math.round((count / limit) * 100)) : 0;
  const isDaily = period_type === 'daily';

  // Build a human "resets in …" string from the backend-provided resets_at.
  let resetsLabel = '';
  if (resets_at) {
    const reset = new Date(resets_at);
    const diffMs = reset.getTime() - Date.now();
    if (isDaily) {
      const hours = Math.max(0, Math.ceil(diffMs / 3_600_000));
      resetsLabel = hours <= 1 ? 'Resets soon' : `Resets in ${hours}h`;
    } else {
      const days = Math.max(0, Math.ceil(diffMs / 86_400_000));
      resetsLabel = `Resets in ${days} ${days === 1 ? 'day' : 'days'}`;
    }
  }

  // Pick palette by how close we are to the cap.
  const tier =
    percent >= 100 ? 'maxed'
    : percent >= 85 ? 'critical'
    : percent >= 60 ? 'warning'
    : 'ok';

  const palette = {
    ok: { bar: 'bg-emerald-500', text: 'text-emerald-400', glow: 'shadow-emerald-500/20' },
    warning: { bar: 'bg-amber-500', text: 'text-amber-400', glow: 'shadow-amber-500/20' },
    critical: { bar: 'bg-rose-500', text: 'text-rose-400', glow: 'shadow-rose-500/30' },
    maxed: { bar: 'bg-rose-600', text: 'text-rose-500', glow: 'shadow-rose-500/40' },
  }[tier];

  const planLabel = plan === 'trial' ? 'Free trial' : plan === 'basic' ? 'Basic' : plan === 'pro' ? 'Pro' : plan;
  const showUpgrade = (plan === 'trial' || plan === 'basic') && tier !== 'ok';

  return (
    <div
      className={`mx-auto max-w-2xl mb-6 rounded-xl border border-border/60 bg-card/40 backdrop-blur px-4 py-3 shadow-sm ${palette.glow}`}
      data-testid="usage-meter"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 text-sm">
          <Gauge className={`w-4 h-4 ${palette.text}`} />
          <span className="text-foreground font-medium" data-testid="usage-meter-count">
            {count} / {limit}
          </span>
          <span className="text-muted-foreground text-xs">
            {isDaily ? 'generations today' : 'generations this month'} · {planLabel}
          </span>
        </div>
        <div className="text-xs text-muted-foreground" data-testid="usage-meter-reset">
          {resetsLabel}
        </div>
      </div>

      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${palette.bar}`}
          style={{ width: `${percent}%` }}
          data-testid="usage-meter-bar"
        />
      </div>

      {showUpgrade && (
        <div className="mt-2 flex items-center justify-between gap-3 text-xs">
          <span className={palette.text}>
            {tier === 'maxed'
              ? (isDaily ? `You're out of generations for today.` : `You're out of generations for this month.`)
              : (isDaily ? `${remaining} left today.` : `${remaining} left this month.`)}
          </span>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300 font-medium underline-offset-4 hover:underline"
            data-testid="usage-meter-upgrade-link"
          >
            {plan === 'trial' ? <Zap className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
            {plan === 'trial' ? 'Pick a plan' : 'Upgrade to Pro'}
          </Link>
        </div>
      )}
    </div>
  );
};

export default UsageMeter;
